/**
 * Copyright (c) 2018-2019 mol* contributors, licensed under MIT, See LICENSE file for more info.
 *
 * @author Alexander Rose <alexander.rose@weirdbyte.de>
 */
import { __assign } from "tslib";
import { Unit, StructureProperties, StructureElement, Bond } from '../../mol-model/structure';
import { Color } from '../../mol-util/color';
import { ParamDefinition as PD } from '../../mol-util/param-definition';
import { getPalette, getPaletteParams } from '../../mol-util/color/palette';
import { Segmentation } from '../../mol-data/int';
import { ColorLists, getColorListFromName } from '../../mol-util/color/lists';
var DefaultList = 'dark-2';
var DefaultColor = Color(0xFAFAFA);
var Description = 'Gives every polymer chain a color based on its `asym_id` value.';
export var PolymerIdColorThemeParams = __assign({}, getPaletteParams({
  type: 'colors',
  colorList: DefaultList
}));
export function getPolymerIdColorThemeParams(ctx) {
  var params = PD.clone(PolymerIdColorThemeParams);

  if (ctx.structure) {
    if (getPolymerAsymIdSerialMap(ctx.structure.root).size > ColorLists[DefaultList].list.length) {
      params.palette.defaultValue.name = 'colors';
      params.palette.defaultValue.params = __assign(__assign({}, params.palette.defaultValue.params), {
        list: {
          kind: 'interpolate',
          colors: getColorListFromName(DefaultList).list
        }
      });
    }
  }

  return params;
}

function getAsymId(unit) {
  switch (unit.kind) {
    case 0
    /* Atomic */
    :
      return StructureProperties.chain.label_asym_id;

    case 1
    /* Spheres */
    :
    case 2
    /* Gaussians */
    :
      return StructureProperties.coarse.asym_id;
  }
}

function getPolymerAsymIdSerialMap(structure) {
  var map = new Map();

  for (var i = 0, il = structure.unitSymmetryGroups.length; i < il; ++i) {
    var unit = structure.unitSymmetryGroups[i].units[0];
    var model = unit.model;

    if (Unit.isAtomic(unit)) {
      var _a = model.atomicHierarchy,
          chainAtomSegments = _a.chainAtomSegments,
          chains = _a.chains;
      var chainIt = Segmentation.transientSegments(chainAtomSegments, unit.elements);

      while (chainIt.hasNext) {
        var chainIndex = chainIt.move().index;
        var entityId = chains.label_entity_id.value(chainIndex);
        var eI = model.entities.getEntityIndex(entityId);

        if (model.entities.data.type.value(eI) === 'polymer') {
          var asymId = chains.label_asym_id.value(chainIndex);
          if (!map.has(asymId)) map.set(asymId, map.size);
        }
      }
    } else if (Unit.isCoarse(unit)) {
      var _b = Unit.isSpheres(unit) ? model.coarseHierarchy.spheres : model.coarseHierarchy.gaussians,
          chainElementSegments = _b.chainElementSegments,
          asym_id = _b.asym_id,
          entity_id = _b.entity_id;

      var chainIt = Segmentation.transientSegments(chainElementSegments, unit.elements);

      while (chainIt.hasNext) {
        var chainIndex = chainIt.move().index;
        var elementIndex = chainElementSegments.offsets[chainIndex];
        var entityId = entity_id.value(elementIndex);
        var eI = model.entities.getEntityIndex(entityId);

        if (model.entities.data.type.value(eI) === 'polymer') {
          var asymId = asym_id.value(elementIndex);
          if (!map.has(asymId)) map.set(asymId, map.size);
        }
      }
    }
  }

  return map;
}

export function PolymerIdColorTheme(ctx, props) {
  var color;
  var legend;

  if (ctx.structure) {
    var l_1 = StructureElement.Location.create(ctx.structure);
    var polymerAsymIdSerialMap_1 = getPolymerAsymIdSerialMap(ctx.structure.root);
    var labelTable_1 = Array.from(polymerAsymIdSerialMap_1.keys());

    var valueLabel = function (i) {
      return labelTable_1[i];
    };

    var palette_1 = getPalette(polymerAsymIdSerialMap_1.size, props, {
      valueLabel: valueLabel
    });
    legend = palette_1.legend;

    color = function (location) {
      var serial = undefined;

      if (StructureElement.Location.is(location)) {
        var asym_id = getAsymId(location.unit);
        serial = polymerAsymIdSerialMap_1.get(asym_id(location));
      } else if (Bond.isLocation(location)) {
        var asym_id = getAsymId(location.aUnit);
        l_1.unit = location.aUnit;
        l_1.element = location.aUnit.elements[location.aIndex];
        serial = polymerAsymIdSerialMap_1.get(asym_id(l_1));
      }

      return serial === undefined ? DefaultColor : palette_1.color(serial);
    };
  } else {
    color = function () {
      return DefaultColor;
    };
  }

  return {
    factory: PolymerIdColorTheme,
    granularity: 'group',
    color: color,
    props: props,
    description: Description,
    legend: legend
  };
}
export var PolymerIdColorThemeProvider = {
  name: 'polymer-id',
  label: 'Polymer Chain Id',
  category: "Chain Property"
  /* Chain */
  ,
  factory: PolymerIdColorTheme,
  getParams: getPolymerIdColorThemeParams,
  defaultValues: PD.getDefaultValues(PolymerIdColorThemeParams),
  isApplicable: function (ctx) {
    return !!ctx.structure;
  }
};